// The client talks to Edge Functions only — never to tables directly.
const BASE = import.meta.env.VITE_SUPABASE_URL as string | undefined;
const ANON = import.meta.env.VITE_SUPABASE_ANON_KEY as string | undefined;

/**
 * A build shipped without env vars must NOT look like a permanent per-guest
 * failure. Left unchecked, `undefined/functions/v1/register` resolves against
 * the page origin, 404s, and parks every guest as non-retryable `failed` —
 * so a five-minute config fix would still lose the whole event's registrations.
 * Treating it as retryable means the queue drains itself once config lands.
 */
function configError(): ApiError | null {
  const missing = [
    !BASE && "VITE_SUPABASE_URL",
    !ANON && "VITE_SUPABASE_ANON_KEY",
  ].filter(Boolean);
  return missing.length
    ? new ApiError(`config missing: ${missing.join(", ")}`, 0, true)
    : null;
}

export class ApiError extends Error {
  constructor(
    message: string,
    readonly status: number,
    /** 4xx (except 408/429) means retrying the same body will fail again. */
    readonly retryable: boolean,
  ) {
    super(message);
    this.name = "ApiError";
  }
}

async function call<T>(
  path: string,
  init: RequestInit & { staffToken?: string } = {},
): Promise<T> {
  const misconfigured = configError();
  if (misconfigured) throw misconfigured;
  // configError() guarantees both are set past this point.
  const base = BASE as string;
  const anon = ANON as string;

  const { staffToken, ...rest } = init;
  const headers: Record<string, string> = {
    // verify_jwt is on for every function; the anon key satisfies it.
    Authorization: `Bearer ${anon}`,
    apikey: anon,
    ...(rest.body ? { "Content-Type": "application/json" } : {}),
    // Named-operator session token, minted by staff-login. Read at call time,
    // never captured, so a re-login mid-session takes effect immediately.
    ...(staffToken ? { "x-staff-token": staffToken } : {}),
    ...((rest.headers as Record<string, string>) ?? {}),
  };

  let res: Response;
  try {
    res = await fetch(`${base}/functions/v1/${path}`, { ...rest, headers });
  } catch (e) {
    // Offline / DNS / TLS — always worth another try later.
    throw new ApiError(`network: ${String(e)}`, 0, true);
  }

  if (!res.ok) {
    const body = await res.text().catch(() => "");
    const retryable = res.status >= 500 || res.status === 408 || res.status === 429;
    throw new ApiError(
      `${path} ${res.status}: ${body.slice(0, 300)}`,
      res.status,
      retryable,
    );
  }

  return res.status === 204 ? (undefined as T) : (await res.json() as T);
}


/* ------------------------------ staff + uploads ----------------------------- */

export interface SignedTarget {
  path: string;
  token: string;
  signedUrl: string;
}

export interface BackdropTargets {
  mediaId: string;
  bucket: "photos";
  web: SignedTarget;
  thumb: SignedTarget;
}

/**
 * A camera shot from the backdrop, or an image generated elsewhere and brought
 * in from the laptop. Identical mechanism, different provenance label — the
 * server keeps them one code path so the thumbnail, upsert and retry behaviour
 * cannot drift apart between them.
 */
export function issueUpload(
  args: {
    guestCode: string;
    mediaId: string;
    bytes: number;
    contentType: string;
    kind: "backdrop" | "external";
  },
  staffToken: string,
): Promise<BackdropTargets> {
  const { kind, ...rest } = args;
  return call<BackdropTargets>("upload-url", {
    method: "POST",
    staffToken,
    body: JSON.stringify({ action: `issue-${kind}`, ...rest }),
  });
}

export function confirmUpload(
  mediaId: string,
  staffToken: string,
): Promise<{ id: string; status: string }> {
  return call("upload-url", {
    method: "POST",
    staffToken,
    body: JSON.stringify({ action: "confirm", mediaId }),
  });
}

/**
 * PUTs the blob to a signed upload URL.
 *
 * Done with fetch rather than supabase-js storage so the queue owns its own
 * retry semantics and error classification end to end.
 */
export async function putSigned(
  target: SignedTarget,
  blob: Blob,
  contentType: string,
): Promise<void> {
  let res: Response;
  try {
    res = await fetch(target.signedUrl, {
      method: "PUT",
      headers: {
        "Content-Type": contentType,
        "x-upsert": "true",
        Authorization: `Bearer ${target.token}`,
      },
      body: blob,
    });
  } catch (e) {
    throw new ApiError(`upload network: ${String(e)}`, 0, true);
  }
  if (!res.ok) {
    const body = await res.text().catch(() => "");
    throw new ApiError(
      `upload ${res.status}: ${body.slice(0, 200)}`,
      res.status,
      res.status >= 500 || res.status === 408 || res.status === 429,
    );
  }
}

export function staffHide(
  mediaId: string,
  hidden: boolean,
  staffToken: string,
): Promise<{ id: string; status: string }> {
  return call("staff-hide", {
    method: "POST",
    staffToken,
    body: JSON.stringify({ mediaId, hidden }),
  });
}

export interface StaffMedia {
  id: string;
  /** 'external' is an image generated outside this system and brought in. */
  origin: "backdrop" | "ai" | "external";
  status: string;
  url: string | null;
  thumbUrl: string | null;
  createdAt: string;
  onWall: boolean;
  /**
   * Who uploaded it, or — once it is on the wall — who approved it. Null on
   * rows written before named operators existed; those are deliberately not
   * backfilled, because there was no actor and inventing one would be a lie.
   */
  operator: string | null;
}

export interface StaffAiJob {
  id: string;
  status: "queued" | "running" | "done" | "failed";
  attempts: number;
  error: string | null;
  createdAt: string;
  /** What the worker actually used. AI_MODEL can change mid-event. */
  model: string | null;
  latencyMs: number | null;
  operator: string | null;
}

export function staffGuestDetail(
  code: string,
  staffToken: string,
): Promise<{
  guest: { id: string; code: string; name: string };
  media: StaffMedia[];
  jobs: StaffAiJob[];
}> {
  return call(`staff-guests?code=${encodeURIComponent(code)}&detail=1`, {
    staffToken,
  });
}

/**
 * Queue an AI portrait from a photo already in the booth's grid. Staff only,
 * and always from a source photo — there is no guest selfie path any more.
 */
export function createAiJob(
  args: { guestCode: string; sourceMediaId: string },
  staffToken: string,
): Promise<{ jobId: string; status: string }> {
  return call("ai-job", {
    method: "POST",
    staffToken,
    body: JSON.stringify(args),
  });
}

export function staffRerunAi(
  jobId: string,
  staffToken: string,
): Promise<{ jobId: string; status: string }> {
  return call("staff-rerun-ai", {
    method: "POST",
    staffToken,
    body: JSON.stringify({ jobId }),
  });
}

/* --------------------------------- the wall -------------------------------- */

export interface WallPhoto {
  id: string;
  origin: "backdrop" | "ai";
  url: string;
  createdAt: string;
}

export function wallFeed(limit = 24): Promise<{ photos: WallPhoto[]; count: number }> {
  return call(`wall?limit=${limit}`);
}


/** Staff approval — the second gate. 403 NO_WALL_CONSENT if the guest hasn't opted in. */
export function staffWall(
  mediaId: string,
  onWall: boolean,
  staffToken: string,
): Promise<{ id: string; onWall: boolean; approvedBy: string | null }> {
  return call("staff-wall", {
    method: "POST",
    staffToken,
    body: JSON.stringify({ mediaId, onWall }),
  });
}

export function staffReassign(
  mediaIds: string[],
  toGuestCode: string,
  staffToken: string,
): Promise<{ moved: number; toGuest: { code: string; name: string } }> {
  return call("staff-reassign", {
    method: "POST",
    staffToken,
    body: JSON.stringify({ mediaIds, toGuestCode }),
  });
}

/* ---------------------------------- staff auth ---------------------------------- */

export type StaffRole = "operator" | "admin";

export interface StaffUser {
  id: string;
  username: string;
  displayName: string;
  role: StaffRole;
  /** STANDA / STANDB for an operator; null for an admin, who picks. */
  boothCode: string | null;
}

export interface StaffSession {
  token: string;
  /** ISO. The token is refused server-side past this, so the console can warn. */
  expiresAt: string;
  user: StaffUser;
}

/**
 * Exchanges a username and password for a session token.
 *
 * Every failure — unknown user, wrong password, password never set, account
 * deactivated — comes back as the same 401 with the same message, and takes the
 * same time server-side. Do not try to be more helpful than that in the UI.
 */
export function staffLogin(username: string, password: string): Promise<StaffSession> {
  return call("staff-login", {
    method: "POST",
    body: JSON.stringify({ username, password }),
  });
}
